import React, { useMemo, useState } from 'react'
import { StyleSheet, Text, View } from 'react-native'
import { RadioButton } from '@linqpal/common-frontend/src/ui'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { TouchableWithoutFeedback } from 'react-native-gesture-handler'
import ExpandableView from '../molecules/ExpandableView'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  IconExclamationRound,
} from '../../assets/icons'
import numbro from 'numbro'
import moment from 'moment'
import isInteger from 'lodash/isInteger'
import { Trans, useTranslation } from 'react-i18next'
import { Spacer } from './'
import { frequency } from '@linqpal/models/src/dictionaries'
import { useResponsive } from '../../utils/hooks'

interface DateWillBeFinalizedProps {
  customStyle?: any
  showInfo?: boolean
}

interface ViewWithDatesProps {
  forSummaryPage?: boolean
  expanded: boolean
  dueDates: Array<string>
}

interface PaymentPlanCardProps {
  onPress: () => void
  paymentOption: any
  paymentAmount: number
  selected: boolean
  forSummaryPage?: boolean
}

const DueDatePill = ({ dueDate }) => {
  return (
    <View
      style={{
        borderRadius: 25,
        backgroundColor: 'rgba(230, 235, 238, 0.45)',
        width: 57,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 6,
        marginRight: 6,
      }}
    >
      <Text style={{ fontSize: 11, fontWeight: '500', color: '#668598' }}>
        {dueDate}
      </Text>
    </View>
  )
}

const Fee = ({ paymentOption, selected, onePay = false }) => {
  const { t } = useTranslation('global')
  const { fee, days } = paymentOption

  const feeToDisplay = useMemo(() => {
    const months = days / 30
    const monthlyFee = fee / months
    return `${
      isInteger(monthlyFee)
        ? monthlyFee
        : isInteger(monthlyFee * 100)
        ? monthlyFee.toFixed(1)
        : monthlyFee.toFixed(2)
    }% `
  }, [days, fee])

  const text = onePay ? (
    <>{t('PaymentPlanCard.monthly-fee', { feeToDisplay })}</>
  ) : (
    <Trans
      t={t}
      i18nKey="PaymentPlanCard.fee-per-month"
      values={{ feeToDisplay }}
      components={{
        feeStyle: <Text style={{ fontSize: 14 }} />,
        monthStyle: <Text style={{ fontWeight: '500', fontSize: 12 }} />,
      }}
    />
  )
  return (
    <View
      style={composeStyle(
        {
          backgroundColor: 'rgba(0, 160, 243, 0.1)',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 5,
          height: 28,
          paddingVertical: 4,
        },
        selected && { backgroundColor: '#003353' },
      )}
    >
      <Text
        style={{
          fontSize: 12,
          fontWeight: '700',
          color: selected ? 'white' : '#003353',
          paddingHorizontal: 11,
        }}
      >
        {text}
      </Text>
    </View>
  )
}

function getDueDates(term, days) {
  const dueDates: Array<string> = []
  let paymentDate
  if (term === 1) {
    paymentDate = moment().add(days, 'days')
    dueDates.push(paymentDate.format('MMM DD'))
  }

  for (let i = 0; i < term - 1; i++) {
    if (!i) paymentDate = moment().add(30, 'days')
    else paymentDate = paymentDate.add(1, 'weeks')
    dueDates.push(paymentDate.format('MMM DD'))
  }

  if (term === 5 || term === 9 || term === 13) {
    paymentDate = moment().add(days, 'days')
    dueDates.push(paymentDate.format('MMM DD'))
  }
  return dueDates
}

const Radio = ({ days, selected, testID }) => {
  const { t } = useTranslation('global')
  const { sm } = useResponsive()
  return (
    <RadioButton
      checked={selected}
      containerStyle={{ paddingVertical: 0 }}
      label={
        <Text
          style={{
            fontWeight: '700',
            fontSize: sm ? 24 : 22,
            lineHeight: 24,
            color: '#003353',
          }}
        >
          {t('PaymentPlanCard.radio-days', { days })}
        </Text>
      }
      radioProps={{
        style: { height: 21, width: 21, borderRadius: 12 },
        innerStyle: { height: 14, width: 14, borderRadius: 8 },
        uncheckedColor: '#99ADBA',
        checkedColor: '#33B3F5',
      }}
      testID={testID}
    />
  )
}

const SampleSchedule = ({ expanded, setExpanded, style = {} }) => {
  const { t } = useTranslation('global')
  return (
    <Text
      onPress={() => setExpanded(!expanded)}
      style={composeStyle(
        { fontSize: 12, fontWeight: '500', lineHeight: 20, color: '#668598' },
        style,
      )}
    >
      <Trans
        t={t}
        i18nKey="PaymentPlanCard.sample-schedule"
        components={{
          Icon: expanded ? <ChevronUpIcon /> : <ChevronDownIcon />,
        }}
      />
    </Text>
  )
}

export const DateWillBeFinalized: React.FC<DateWillBeFinalizedProps> = ({
  customStyle = {},
  showInfo = true,
}) => {
  const { t } = useTranslation('global')
  return (
    <View
      style={composeStyle(styles.dateWillBeFinalizedView, customStyle.view)}
    >
      {showInfo && <IconExclamationRound />}
      <Text
        style={composeStyle(
          styles.dateWillBeFinalizedText,
          { color: '#335C75' },
          customStyle.text,
        )}
      >
        {t('PaymentPlanCard.dates-finalized-when-loan-disbursed')}
      </Text>
    </View>
  )
}

const ViewWithDates: React.FC<ViewWithDatesProps> = ({
  forSummaryPage,
  expanded,
  dueDates,
}) => {
  return (
    <ExpandableView
      opened={expanded}
      titleStyle={{ fontWeight: '500', color: '#668598', fontSize: 14 }}
      collapsedContainerStyle={{
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
      }}
      collapsableContent={
        <View
          style={{
            backgroundColor: forSummaryPage ? 'white' : '#F8F9F9',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            paddingHorizontal: forSummaryPage ? 0 : 20,
            paddingVertical: 10,
            maxWidth: 600,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginVertical: 5,
            }}
          >
            {dueDates.map((date) => (
              <DueDatePill key={date} dueDate={date} />
            ))}
          </View>
          {!forSummaryPage && <DateWillBeFinalized />}
        </View>
      }
    />
  )
}

const PaymentPlanContainer = ({ children, selected, onPress }) => {
  return (
    <View
      style={composeStyle(
        styles.rootView,
        selected && {
          backgroundColor: '#F1FAFF',
          borderColor: '#00A0F3',
          borderWidth: 2,
        },
      )}
    >
      <TouchableWithoutFeedback onPress={() => onPress()}>
        {children}
      </TouchableWithoutFeedback>
    </View>
  )
}

const SummaryPlanContainer = ({
  children,
  paymentOption,
  onePay,
  dueDates,
}) => {
  const { days } = paymentOption
  const { t } = useTranslation('global')
  const [expanded, setExpanded] = useState(false)
  return (
    <>
      <View style={styles.summaryRootView}>
        <View style={styles.summarySecondaryView}>
          <View style={{ flexDirection: 'column', marginBottom: 18 }}>
            <Text
              style={{
                fontWeight: '700',
                fontSize: 26,
                lineHeight: 36,
                marginBottom: 8,
              }}
            >
              {t('PaymentPlanCard.term-on-pay-invoice-modal', { days })}
            </Text>
            <Fee paymentOption={paymentOption} selected onePay={onePay} />
          </View>
          {children}
        </View>
      </View>
      <View style={{ marginTop: 16, marginLeft: 20 }}>
        <SampleSchedule expanded={expanded} setExpanded={setExpanded} />
        <ViewWithDates dueDates={dueDates} expanded={expanded} forSummaryPage />
        <Spacer />
        <DateWillBeFinalized />
      </View>
    </>
  )
}

const SummaryPlan = ({
  paymentOption,
  onePay,
  totalPayment,
  weeklyPayments,
  dueDates,
}) => {
  const { term } = paymentOption
  return (
    <SummaryPlanContainer
      paymentOption={paymentOption}
      onePay={onePay}
      dueDates={dueDates}
    >
      <View style={{ flexDirection: 'column', marginTop: 3 }}>
        <Text
          style={{
            paddingBottom: 5,
            color: '#335C75',
            fontWeight: '500',
            fontSize: 15,
            lineHeight: 21,
          }}
        >
          {term === 1 ? 'One-time payment of' : `${term} weekly payments of`}
        </Text>
        <Text style={styles.summaryAmountStyle}>
          $
          {numbro(term === 1 ? totalPayment : weeklyPayments).format({
            thousandSeparated: true,
            mantissa: 2,
          })}
        </Text>
      </View>
    </SummaryPlanContainer>
  )
}

const OnePaySummaryPlan = ({
  paymentOption,
  onePay,
  totalPayment,
  weeklyPayments,
  dueDates,
}) => {
  const { term, days } = paymentOption
  const { t } = useTranslation('global')
  console.log()

  return (
    <SummaryPlanContainer
      paymentOption={paymentOption}
      onePay={onePay}
      dueDates={dueDates}
    >
      <View style={{ flexDirection: 'column', marginTop: 3 }}>
        <Text
          style={{
            fontWeight: '700',
            fontSize: 20,
            lineHeight: 24,
            color: '#003353',
            alignSelf: 'flex-end',
          }}
        >
          $
          {numbro(term === 1 ? totalPayment : weeklyPayments).format({
            thousandSeparated: true,
            mantissa: 2,
          })}
        </Text>
        <Text
          style={{
            marginTop: 8,
            paddingBottom: 5,
            color: '#335C75',
            fontWeight: '500',
            fontSize: 12,
            lineHeight: 17,
            width: 121,
            textAlign: 'right',
            alignSelf: 'flex-end',
          }}
        >
          {t('PaymentPlanCard.one-time-due-on-day', { days })}
        </Text>
      </View>
    </SummaryPlanContainer>
  )
}

const Regular = ({
  selected,
  onPress,
  paymentOption,
  paymentAmount,
  dueDates,
  weeklyPayments,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { days, term } = paymentOption
  const { sm } = useResponsive()
  return (
    <>
      <PaymentPlanContainer selected={selected} onPress={onPress}>
        <View style={styles.secondaryView}>
          <Radio selected={selected} days={days} testID={paymentOption._id} />
          <View
            style={{
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <Text
              style={{
                paddingBottom: 5,
                color: '#335C75',
                fontWeight: '500',
                fontSize: 12,
                lineHeight: 21,
              }}
            >
              {term === 1
                ? 'One-time payment of'
                : `${term} weekly payments of`}
            </Text>
            <Text style={styles.amountStyle}>
              $
              {numbro(term === 1 ? paymentAmount : weeklyPayments).format({
                thousandSeparated: true,
                mantissa: 2,
              })}
            </Text>
          </View>
        </View>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingHorizontal: sm ? 20 : 16,
            marginBottom: 18,
          }}
        >
          <Fee paymentOption={paymentOption} selected={selected} />
          <SampleSchedule expanded={expanded} setExpanded={setExpanded} />
        </View>
        <ViewWithDates dueDates={dueDates} expanded={expanded} />
      </PaymentPlanContainer>
    </>
  )
}

const OnePayCard = ({
  selected,
  onPress,
  paymentOption,
  dueDates,
  weeklyPayments,
  totalPayment,
}) => {
  const { t } = useTranslation('global')
  const [expanded, setExpanded] = useState(false)
  const { days, term } = paymentOption
  return (
    <PaymentPlanContainer selected={selected} onPress={onPress}>
      <View
        style={composeStyle(styles.secondaryView, styles.onePaySecondaryView)}
      >
        <Radio selected={selected} days={days} testID={paymentOption._id} />
        <View
          style={{
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <Text style={[styles.onePayAmountStyle, { fontSize: 18 }]}>
            $
            {numbro(term === 1 ? totalPayment : weeklyPayments).format({
              thousandSeparated: true,
              mantissa: 2,
            })}
          </Text>
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingHorizontal: 15,
          marginBottom: 9,
        }}
      >
        <Text
          style={{
            fontWeight: '500',
            fontSize: 12,
            marginLeft: 30,
            lineHeight: 17,
            color: '#335C75',
          }}
        >
          {t('PaymentPlanCard.one-time-due-on-day', { days })}
        </Text>
        <Fee paymentOption={paymentOption} selected={selected} onePay />
      </View>
      <View
        style={{
          alignItems: 'flex-end',
          paddingHorizontal: 15,
          paddingBottom: 15,
        }}
      >
        <SampleSchedule expanded={expanded} setExpanded={setExpanded} />
      </View>
      <ViewWithDates dueDates={dueDates} expanded={expanded} />
    </PaymentPlanContainer>
  )
}

export const PaymentPlanCard: React.FC<PaymentPlanCardProps> = ({
  ...props
}) => {
  const { paymentAmount, paymentOption } = props
  const { days, term } = paymentOption
  const onePay = paymentOption.frequency === frequency.SINGLE

  const totalPayment =
    paymentAmount + Math.round(paymentAmount * paymentOption.fee) / 100
  const weeklyPayments = Math.round((totalPayment / term) * 100) / 100
  const dueDates = getDueDates(term, days)

  const summaryProps = {
    paymentOption,
    onePay,
    totalPayment,
    weeklyPayments,
    dueDates,
  }
  const cardProps = { ...props, totalPayment, weeklyPayments, dueDates }

  if (props.forSummaryPage && onePay)
    return <OnePaySummaryPlan {...summaryProps} />
  else if (props.forSummaryPage) return <SummaryPlan {...summaryProps} />
  else if (onePay) return <OnePayCard {...cardProps} />
  else return <Regular {...cardProps} />
}

const styles = StyleSheet.create({
  rootView: {
    borderWidth: 1,
    borderColor: '#E6EBEE',
    borderRadius: 10,
    justifyContent: 'space-between',
  },
  secondaryView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
  },
  summarySecondaryView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  summaryRootView: {
    justifyContent: 'space-between',
    backgroundColor: '#F1FAFF',
  },
  paymentText: {
    paddingBottom: 5,
    color: '#335C75',
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 21,
  },
  summaryPaymentText: {
    paddingBottom: 5,
    color: '#668598',
    fontWeight: '500',
    fontSize: 15,
    lineHeight: 21,
  },
  dateWillBeFinalizedView: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  dateWillBeFinalizedText: {
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 20,
    marginLeft: 5,
    color: '#0070AA',
  },
  onePayAmountStyle: {
    fontWeight: '700',
    color: '#003353',
    lineHeight: 36,
  },
  summaryAmountStyle: {
    fontWeight: '600',
    color: '#335C75',
    fontSize: 18,
    lineHeight: 24,
    alignSelf: 'flex-end',
  },
  amountStyle: {
    fontWeight: '600',
    color: '#003353',
    fontSize: 18,
    lineHeight: 24,
  },
  onePaySecondaryView: {
    padding: 15,
    paddingBottom: 4,
  },
})
